import { useCallback, useEffect, useRef } from 'react';
import { useCheckIfSessionExists } from 'hooks/useCheckIfSessionExists';

type TUseTabRelocationOptions = {
  onSuccess?: () => void;
  onError?: () => void;
};

const noop = () => {};

const HANDSHAKE_MAX_ATTEMPTS = 600;

export const useTabRelocation = ({ onSuccess = noop, onError = noop }: TUseTabRelocationOptions = {}) => {
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkIfSessionExists = useCheckIfSessionExists();

  useEffect(() => {
    const onMessageListening = (event: MessageEvent) => {
      const devOrigins = ['http://localhost:3001'];
      const prodOrigins = [import.meta.env.REACT_APP_ADMIN_HOSTNAME];
      const origins = import.meta.env.MODE === 'development' ? devOrigins : prodOrigins;
      if (!origins.includes(event.origin)) {
        return;
      }
      if (event.data === 'received' && intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
    window.addEventListener('message', onMessageListening);
    return () => {
      window.removeEventListener('message', onMessageListening);
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, []);

  const refetch = useCallback(
    async (to: string) => {
      await checkIfSessionExists();
      const admin = JSON.parse(localStorage.getItem('user') || 'null');
      const redirectionLink =
        import.meta.env.MODE === 'development' ? 'http://localhost:3001' : import.meta.env.REACT_APP_ADMIN_HOSTNAME;
      const newWindow = window.open(`${redirectionLink}/redirect`, '_blank');
      if (!newWindow) {
        onError();
        return;
      }
      let attempts = 0;
      const id = setInterval(() => {
        attempts += 1;
        if (newWindow.closed || attempts > HANDSHAKE_MAX_ATTEMPTS) {
          clearInterval(id);
          return;
        }
        newWindow.postMessage(JSON.stringify({ ...admin, to }), `${redirectionLink}/redirect`);
        onSuccess();
      }, 100);
      intervalId.current = id;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { refetch };
};
